import { IconFilePlus, IconSparkles } from "@tabler/icons-react"
import { type FC, useState } from "react"
import styled from "styled-components"

import { type FCC } from "@forento/ui/types/react"

import Button, { SubmitButton } from "~/components/Button"
import Checkbox from "~/components/Checkbox"
import InputField from "~/components/InputField"
import {
	DefaultModal,
	ModalButtons,
	ModalContentPadding,
	ModalHeader,
	ModalHelpCenter,
	ModalHelpCenterIcon,
	ModalInputs,
	ModalTitle,
} from "~/components/Modal"
import { useAlert } from "~/contexts/AlertContext"
import { usePlatform } from "~/contexts/UserContext"
import { captureForentoAiEvent, useForentoAiImpression } from "~/utilities/forentoAiAnalytics"
import { query } from "~/utilities/trpc"

import Ai from "./Ai"

type Props = { isOpen: boolean; initialOption?: "ai"; closeModal(): void; onSubmitted(): void }
const CreateNewCourseModal: FC<Props> = ({ isOpen, initialOption, closeModal, onSubmitted }) => {
	const platform = usePlatform()!
	const alert = useAlert()

	const [selectedOption, setSelectedOption] = useState<"ai" | "empty" | null>(() =>
		platform.enabledFeatures.includes("ai-generated-courses") ? (initialOption ?? null) : "empty",
	)

	const [title, setTitle] = useState("")
	const [shortDescription, setShortDescription] = useState("")
	const [issueCertificates, setIssueCertificates] = useState(false)
	const [commentsEnabled, setCommentsEnabled] = useState(true)
	const [autoApproveComments, setAutoApproveComments] = useState(true)

	useForentoAiImpression("forento_ai_card_viewed", { surface: "create-course" }, isOpen && selectedOption === null)

	const create = query.course.create.useMutation({
		async onError(error) {
			console.error(error)
			await alert.show(
				"Failed to create course",
				"An error occurred while creating the course. Please try again.",
			)
		},
		onSuccess() {
			setSelectedOption(platform.enabledFeatures.includes("ai-generated-courses") ? null : "empty")
			setTitle("")
			setShortDescription("")
			setIssueCertificates(false)
			setCommentsEnabled(true)
			setAutoApproveComments(true)
			onSubmitted()
		},
	})

	if (selectedOption === "ai") {
		return <Ai isOpen={isOpen} onBack={() => setSelectedOption(null)} onClose={onSubmitted} />
	}

	if (selectedOption === "empty") {
		return (
			<Wrapper
				onClose={closeModal}
				wide
				isOpen={isOpen}
				onSubmit={() =>
					create.mutate({ title, shortDescription, issueCertificates, commentsEnabled, autoApproveComments })
				}
			>
				<ModalInputs>
					<InputField label="Title" value={title} onChange={setTitle} />
					<InputField label="Short description" value={shortDescription} onChange={setShortDescription} />
					<Checkbox
						label="Issue certificates"
						isChecked={issueCertificates}
						onChange={setIssueCertificates}
						isDisabled={!platform!.plan.access.certificates}
					/>
					<Checkbox label="Enable comments" isChecked={commentsEnabled} onChange={setCommentsEnabled} />
					<Checkbox
						label="Automatically approve comments"
						isChecked={autoApproveComments}
						onChange={setAutoApproveComments}
						isDisabled={!commentsEnabled}
					/>
				</ModalInputs>
				<ModalButtons>
					{platform.enabledFeatures.includes("ai-generated-courses") ? (
						<Button variant="secondary" onClick={() => setSelectedOption(null)}>
							Back
						</Button>
					) : (
						<Button variant="secondary" onClick={closeModal}>
							Cancel
						</Button>
					)}
					<SubmitButton
						variant="primary"
						isDisabled={title.trim().length === 0 || shortDescription.trim().length === 0}
					>
						Create course
					</SubmitButton>
				</ModalButtons>
			</Wrapper>
		)
	}

	return (
		<Wrapper isOpen={isOpen} onClose={closeModal}>
			<ModalContentPadding>
				<OptionsContainer>
					<OptionCardWithRibbon
						onClick={() => {
							captureForentoAiEvent("forento_ai_card_clicked", { surface: "create-course" })
							setSelectedOption("ai")
						}}
					>
						<Ribbon>BETA</Ribbon>
						<OptionIcon>
							<IconSparkles className="size-6" />
						</OptionIcon>
						<OptionTitle>Generate with Forento AI</OptionTitle>
						<OptionDescription>
							Generate a course with Forento AI. You can use that as a starting point or completely
							rewrite it.
						</OptionDescription>
					</OptionCardWithRibbon>
					<OptionCard onClick={() => setSelectedOption("empty")}>
						<OptionIcon>
							<IconFilePlus className="size-6" />
						</OptionIcon>
						<OptionTitle>Create empty course</OptionTitle>
						<OptionDescription>
							Create a blank course and build it from scratch with your own content.
						</OptionDescription>
					</OptionCard>
				</OptionsContainer>
			</ModalContentPadding>
			<ModalButtons>
				<Button variant="secondary" onClick={closeModal}>
					Cancel
				</Button>
			</ModalButtons>
		</Wrapper>
	)
}

type WrapperProps = { wide?: boolean; isOpen: boolean; onSubmit?(): void; onClose(): void }
const Wrapper: FCC<WrapperProps> = ({ wide, isOpen, onSubmit, onClose, children }) => (
	<DefaultModal onClose={onClose} width={wide ? 1000 : undefined} isOpen={isOpen} onSubmit={onSubmit}>
		<ModalHeader>
			<ModalTitle>Create a new course</ModalTitle>
			<ModalHelpCenter newTab onClick="https://forento.link/create-course">
				<ModalHelpCenterIcon />
				Learn more
			</ModalHelpCenter>
		</ModalHeader>
		{children}
	</DefaultModal>
)

const OptionsContainer = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 16px;
	margin-bottom: 8px;
`

const OptionCard = styled.button`
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	padding: 24px;
	border: 2px solid #e5e7eb;
	border-radius: 8px;
	background: white;
	cursor: pointer;
	transition: all 0.2s ease;
	gap: 12px;

	&:hover {
		border-color: #3b82f6;
		background: #f8fafc;
	}

	&:active {
		transform: scale(0.98);
	}
`

const OptionCardWithRibbon = styled(OptionCard)`
	position: relative;
	overflow: hidden;
`

const Ribbon = styled.div`
	position: absolute;
	top: 12px;
	right: -24px;
	width: 100px;
	background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
	color: white;
	font-size: 11px;
	font-weight: 700;
	text-align: center;
	padding: 4px 0;
	transform: rotate(45deg);
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	letter-spacing: 0.5px;
`

const OptionIcon = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 48px;
	height: 48px;
	border-radius: 8px;
	background: #f1f5f9;
	color: #3b82f6;
`

const OptionTitle = styled.h3`
	font-weight: 600;
	font-size: 16px;
	color: #101828;
	margin: 0;
`

const OptionDescription = styled.p`
	font-size: 14px;
	color: #6b7280;
	margin: 0;
	line-height: 1.5;
`

export default CreateNewCourseModal
